import { useQueries, useQuery } from '@tanstack/react-query';

import {
  API_AUTOCOMPLETE_FIND_ALL_LIMIT,
  ApiAutocomplete,
  type RequireLabelWithOptionalHelpTooltipOrAria,
  api,
  getApiAutocompleteErrorMessage,
  getQueryProps,
  isDefined,
  isTruthy,
  useDebouncedState,
} from 'common';

type CommonProps = {
  disabled?: boolean;
  placeholder?: string;
  errorMessage?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  name?: string;
  excludedIds?: string[];
} & RequireLabelWithOptionalHelpTooltipOrAria;

type SingleSelectProps = CommonProps & {
  isMultiple: false;
  value?: string;
  onChange: (value?: string) => void;
};
type MultiSelectProps = CommonProps & {
  isMultiple: true;
  value?: string[];
  onChange: (value?: string[]) => void;
};

export type OrganizationAutocompleteProps = SingleSelectProps | MultiSelectProps;

export function OrganizationAutocomplete(props: OrganizationAutocompleteProps) {
  const {
    disabled,
    isMultiple,
    value,
    onChange,
    label,
    'aria-label': ariaLabel,
    placeholder,
    errorMessage,
    onBlur,
    name,
    excludedIds,
    helpTooltipProps,
  } = props;

  const {
    value: inputValue,
    setValue: setInputValue,
    debouncedValue: debouncedSearch,
  } = useDebouncedState({ initialValue: '' });

  const {
    data: organizations,
    isPending: isOrganizationsPending,
    isError: isFindAllError,
  } = useQuery({
    ...getQueryProps(api.client.Organization, 'findAllPaginated', {
      page: { size: API_AUTOCOMPLETE_FIND_ALL_LIMIT },
      q: isTruthy(debouncedSearch) ? debouncedSearch : undefined,
      excludedIds,
    }),
  });

  const {
    data: selectedValueResults,
    isLoading: isSelectedOrganizationsLoading,
    isError: isFindOneError,
  } = useQueries({
    queries: (Array.isArray(value) ? value : [value])
      .filter(isTruthy)
      .map((value) => getQueryProps(api.client.Organization, 'findOneById', value)),
    combine: (results) => {
      const data = results.map((result) => result.data);

      return {
        data: data.every(isDefined) ? data : undefined,
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });

  return (
    <>
      <ApiAutocomplete<api.Organization.Organization, boolean>
        label={label}
        aria-label={ariaLabel ?? 'Organization'}
        multiple={isMultiple}
        inputFieldName={name}
        placeholder={placeholder}
        disabled={disabled}
        loading={isOrganizationsPending || isSelectedOrganizationsLoading}
        inputValue={inputValue}
        setInputValue={setInputValue}
        options={organizations?.data || []}
        value={isMultiple ? (selectedValueResults ?? []) : (selectedValueResults?.[0] ?? null)}
        setValue={(newValue) => {
          if (isMultiple) {
            if (Array.isArray(newValue)) {
              onChange(newValue.map((organization) => organization.id));
            } else {
              onChange([]);
            }
          } else if (!Array.isArray(newValue)) {
            onChange(newValue?.id);
          }
        }}
        onBlur={onBlur}
        errorMessage={getApiAutocompleteErrorMessage({
          errorMessage,
          isFindAllError,
          isFindOneError,
        })}
        helpTooltipProps={helpTooltipProps}
      />
    </>
  );
}
